import { db } from '../../database/schema.js'
import { featureStorage } from '../../storage/feature-storage.js'
import { logger } from '../../utils/logger.js'
import { LitElement, html, css } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { Task } from '@lit/task'
import { router, type RouteMatch } from '../../utils/router.js'
import { themeManager } from '../../services/theme-manager.js'
import { authService } from '../../services/auth/auth-service.js'
import { authConfig } from '../../services/auth/auth-config.js'
import type { AuthState } from '../../services/auth/auth-context.js'
import {
  startBackgroundSync,
  stopBackgroundSync,
  syncUserSettings,
} from '../../storage/sync-manager.js'
import { SettingsStorage } from '../../storage/settings-storage.js'
import { compassService } from '../../services/compass/compass-provider.js'
import { permissionManager } from '../../services/permissions/permission-manager.js'
import {
  routes,
  getCurrentView,
  getCurrentPageTitle,
  needsBackButton,
  viewToPath,
} from '../../lib/route-config.js'
import '../location-search/location-search.js'
import '../map/cairn-map.js'
import '../common/cairn-toast.js'
import '../auth/login-modal.js'

import '@material/web/menu/menu.js'
import '@material/web/menu/menu-item.js'

const log = logger.create('app')

// Other views are lazy-loaded on first navigation
const viewImports: Partial<Record<string, () => Promise<unknown>>> = {
  'cairn-logbook': () => import('../trips/cairn-logbook.js'),
  'plan-form': () => import('../trips/plan-form.js'),
  'plan-detail': () => import('../trips/plan-detail.js'),
  'activity-detail': () => import('../trips/activity-detail.js'),
  'activity-map-page': () => import('../trips/activity-map-page.js'),
  'route-detail': () => import('../trips/route-detail.js'),
  'plan-map': () => import('../trips/plan-map.js'),
  'plan-download': () => import('../trips/plan-download.js'),
  'cairn-teams': () => import('../teams/cairn-teams.js'),
  'cairn-team-detail': () => import('../teams/cairn-team-detail.js'),
  'cairn-team-invite-claim': () => import('../teams/cairn-team-invite-claim.js'),
  'cairn-settings': () => import('../profile/cairn-settings.js'),
  'subscription-success': () => import('../subscription/subscription-success.js'),
  'auth-login': () => import('../auth/auth-login.js'),
  'auth-register': () => import('../auth/auth-register.js'),
  'auth-account-setup': () => import('../auth/auth-account-setup.js'),
  'eula-acceptance': () => import('../auth/eula-acceptance.js'),
  'auth-callback': () => import('../auth/auth-callback.js'),
  'auth-register-passkey': () => import('../auth/auth-register-passkey.js'),
  'cairn-docs': () => import('../docs/cairn-docs.js'),
}
const loadedViews = new Set<string>(['cairn-map'])

@customElement('cairn-app')
export class CairnApp extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      width: 100vw;
      height: 100vh;
      background: var(--cairn-background-color, #f8fafc);
      color: var(--cairn-text-color, #1e293b);
      font-family: var(
        --cairn-font-family,
        -apple-system,
        BlinkMacSystemFont,
        'Segoe UI',
        Roboto,
        sans-serif
      );
      overflow: hidden;
    }

    /* Mobile-specific height using dynamic viewport */
    @media (max-width: 768px) {
      :host {
        height: 100dvh;
        width: 100dvw;
      }
    }

    /* Removed problematic mobile viewport styles that interfere with map rendering */

    .app-header {
      width: 100%;
      background: var(--cairn-surface-color, #ffffff);
      padding: 0 8px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--cairn-spacing-sm, 8px);
      flex-shrink: 0;
      z-index: 1100;
      height: 72px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .app-main {
      flex: 1;
      display: flex;
      overflow: hidden;
      min-height: 0; /* Important for flex children */
    }

    .app-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      min-height: 0; /* Important for flex children */
    }

    .app-navigation {
      flex-shrink: 0;
    }

    /* Mobile: Hide header, add padding for status bar, stack layout */
    @media (max-width: 768px) {
      .app-header {
        display: none;
      }

      .app-main {
        flex-direction: column !important;
        padding-top: env(safe-area-inset-top, 20px);
      }

      .app-content {
        flex: 1 1 0 !important;
        overflow: hidden;
        min-height: 0;
      }

      .app-navigation {
        flex: 0 0 auto !important;
        position: static !important;
      }
    }

    .app-logo {
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-sm, 8px);
      flex-shrink: 0;
      text-decoration: none;
      color: inherit;
      cursor: pointer;
    }

    .app-logo img {
      height: 48px;
      width: 48px;
      flex-shrink: 0;
    }

    .app-title-area {
      flex: 1;
      display: flex;
      align-items: center;
      margin-left: var(--cairn-spacing-lg, 24px);
    }

    .title-content {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .title-left {
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-sm, 8px);
    }

    .app-page-title {
      font-size: var(--cairn-text-size-xl, 24px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
      margin: 0;
    }

    .app-logo-title {
      font-size: var(--cairn-text-size-lg, 20px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
    }

    .back-button {
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      border-radius: var(--cairn-border-radius, 8px);
      width: 48px;
      height: 48px;
      padding: 0;
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
      transition: all var(--cairn-animation-duration, 0.2s);
      flex-shrink: 0;
    }

    .back-button:hover {
      background: rgba(59, 130, 246, 0.04);
      color: var(--cairn-primary-color, #3b82f6);
    }

    .back-button .material-icons {
      font-size: 24px;
      color: inherit;
      font-family: 'Material Symbols Outlined';
    }

    .app-header-actions {
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-md, 16px);
      flex-shrink: 0;
    }

    .header-search {
      width: 350px;
      max-width: 350px;
      position: relative;
    }

    /* Override location search component styling for header integration */
    .header-search location-search {
      --md-outlined-text-field-container-height: 40px;
      --md-outlined-text-field-input-text-size: 14px;
      --md-outlined-text-field-label-text-size: 12px;
      --md-filled-button-container-height: 32px;
      --location-search-border-radius: 30px;
      --md-outlined-field-container-shape: 30px;
      --md-outlined-field-outline-width: 1px;
    }

    /* Direct styling for Material Web field */
    .header-search location-search md-outlined-text-field {
      --md-outlined-field-container-shape: 30px;
      border-radius: 30px;
    }

    /* Ensure search results dropdown appears over content */
    .header-search location-search::part(results-container) {
      z-index: 5000;
    }

    @media (max-width: 768px) {
      .header-search {
        width: 250px;
        max-width: 250px;
      }
    }

    @media (max-width: 640px) {
      .header-search {
        width: 180px;
        max-width: 180px;
      }
    }

    .profile-button {
      display: none; /* Hidden on mobile */
      width: 48px;
      height: 48px;
      border: none;
      background: none;
      cursor: pointer;
      border-radius: var(--cairn-border-radius, 8px);
      color: var(--cairn-text-secondary-color, #64748b);
      transition: all var(--cairn-animation-duration, 0.2s);
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      margin-right: 16px;
    }

    .profile-button:hover {
      background: rgba(59, 130, 246, 0.04);
      color: var(--cairn-primary-color, #3b82f6);
    }

    .profile-button .material-icons {
      font-size: 24px;
      color: inherit;
      font-family: 'Material Symbols Outlined';
    }

    .profile-avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      object-fit: cover;
    }

    .profile-anchor {
      position: relative;
    }

    .app-navigation {
      flex-shrink: 0;
      /* Navigation styles handled by the component */
    }

    .loading {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      gap: var(--cairn-spacing-md, 16px);
      font-size: var(--cairn-text-size-md, 16px);
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .error {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      padding: var(--cairn-spacing-xl, 32px);
      text-align: center;
    }

    .error-message {
      color: var(--cairn-error-color, #dc2626);
      font-size: var(--cairn-text-size-md, 16px);
      margin-bottom: var(--cairn-spacing-md, 16px);
    }

    .retry-button {
      background: var(--cairn-primary-color, #3b82f6);
      color: white;
      border: none;
      padding: var(--cairn-spacing-sm, 8px) var(--cairn-spacing-md, 16px);
      border-radius: var(--cairn-border-radius, 8px);
      cursor: pointer;
      font-size: var(--cairn-text-size-sm, 14px);
      transition: background-color var(--cairn-animation-duration, 0.2s);
    }

    .retry-button:hover {
      background: #2563eb;
    }

    /* Desktop layout */
    @media (min-width: 769px) {
      .app-main {
        padding-top: 0;
        flex-direction: row; /* Row layout: nav + content */
      }

      .app-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        order: 1; /* Move content after navigation */
      }

      .app-navigation {
        order: 0; /* Move navigation before content */
      }

      .app-logo {
        margin-left: 0;
        padding-left: 4px;
      }

      .profile-button {
        display: flex;
      }

      .settings-button {
        display: flex;
      }

      .app-navigation {
        position: static;
        width: 72px;
        flex-shrink: 0;
      }
    }

    /* Safe area handling is handled by the navigation component itself */
  `

  @state()
  private currentRoute: RouteMatch | null = null

  @state()
  private error: string | null = null

  @state()
  private authState: AuthState = authService.getState()

  @state()
  private showProfileMenu = false

  @state()
  private initComplete = false

  private loginModalDismissed = false
  private loginModalChecked = false
  private pendingImportHandled = false

  private unsubAuth?: () => void

  private initTask = new Task(this, {
    task: async (): Promise<void> => {
      try {
        await this.initializeDatabase()
        await this.checkMobilePlatform()
        await authService.initialize()
        await this.loadInitialData()

        const { permissionOnboarding } =
          await import('../../services/permissions/permission-onboarding.js')
        if (permissionOnboarding.needsOnboarding()) {
          await permissionOnboarding.run()
        } else {
          void this.autoPromptMissingPermissions()
        }

        const hasLocationPermission = await compassService.hasLocationPermission()
        log.debug('📍 Location permission result:', hasLocationPermission)

        // Restore any pending import from a previous session (e.g. app killed during login)
        const { pendingImportService } =
          await import('../../services/import/pending-import-service.js')
        await pendingImportService.restoreFromStorage()
        log.debug('initTask done, hasPending:', pendingImportService.hasPending())

        this.initComplete = true
      } catch (error) {
        log.error('Failed to initialize app:', error)
        this.error = error instanceof Error ? error.message : 'Failed to initialize application'
        this.initComplete = true
      }
    },
    args: (): [] => [],
  })

  override connectedCallback(): void {
    super.connectedCallback()

    void this.connectedCallbackAsync()
  }

  private async connectedCallbackAsync(): Promise<void> {
    // Subscribe to auth state changes
    this.unsubAuth = authService.onChange(state => {
      this.authState = state
      if (state.status === 'authenticated') {
        startBackgroundSync()
        void syncUserSettings()
        void import('../../services/moderation/block-store.js').then(({ blockStore }) =>
          blockStore.initialize()
        )
        void this.processPendingImport()
      } else {
        stopBackgroundSync()
      }
    })

    // Listen for shared files queued by the share intent service
    window.addEventListener('pending-import-ready', this.boundHandlePendingImport)

    // Re-init theme when a cross-device settings pull updates the local mirror
    window.addEventListener('user-settings-synced', this.boundHandleUserSettingsSynced)

    // Hide Capacitor splash screen immediately and show our custom splash
    try {
      const { SplashScreen } = await import('@capacitor/splash-screen')
      await SplashScreen.hide()
    } catch (error) {
      // Splash screen plugin not available (web)
    }

    // Set up routes
    router.addRoutes([...routes])

    // Subscribe to route changes
    router.subscribe((route): void => {
      this.currentRoute = route
    })

    // Start the router
    router.start()

    // Listen for navigation events
    this.addEventListener('navigate-back', this.handleNavigateBack.bind(this))

    // Listen for settings updates and propagate to child components
    this.addEventListener('settings-updated', this.handleSettingsUpdate.bind(this) as EventListener)

    // Listen for mobile navigation search events and forward to map
    this.addEventListener(
      'location-search-navigation',
      this.handleMobileLocationSearch.bind(this) as EventListener
    )

    // Register Capacitor App lifecycle listeners for reliable native pause/resume
    void this.setupCapacitorLifecycle()
  }

  private async setupCapacitorLifecycle(): Promise<void> {
    try {
      const { App } = await import('@capacitor/app')

      void App.addListener('pause', (): void => {
        log.debug('📱 App paused (Capacitor)')
        // Dispatch visibilitychange for components that listen to it
        // This ensures compass/GPS power saving kicks in on native platforms
        // even when the browser doesn't fire visibilitychange
        if (document.visibilityState !== 'hidden') {
          document.dispatchEvent(new Event('visibilitychange'))
        }
      })

      void App.addListener('resume', (): void => {
        log.debug('📱 App resumed (Capacitor)')
        if (document.visibilityState !== 'visible') {
          document.dispatchEvent(new Event('visibilitychange'))
        }
        void this.checkShareIntent()
      })

      // Return-to-app after a web-billing checkout opened in the system browser.
      const { registerSubscriptionDeepLink } =
        await import('../../services/subscription/subscription-deeplink.js')
      void registerSubscriptionDeepLink()
    } catch {
      // Capacitor App plugin not available (web) — visibilitychange is sufficient
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.unsubAuth?.()
    window.removeEventListener('pending-import-ready', this.boundHandlePendingImport)
    window.removeEventListener('user-settings-synced', this.boundHandleUserSettingsSynced)
  }

  private async initializeDatabase(): Promise<void> {
    log.info('Initializing database...')
    await this.removeAutoDetectedRestFeatures()
  }

  private async removeAutoDetectedRestFeatures(): Promise<void> {
    const restFeatures = await db.features.where('waypointType').equals('rest').toArray()
    if (restFeatures.length === 0) return
    log.info(`Removing ${restFeatures.length} auto-detected rest features`)
    for (const f of restFeatures) {
      await featureStorage.deleteFeature(f.id)
    }
  }

  private async checkMobilePlatform(): Promise<void> {
    if (typeof window !== 'undefined' && 'Capacitor' in window) {
      const { Capacitor } = await import('@capacitor/core')
      log.info('Running on:', Capacitor.getPlatform())

      if (Capacitor.isNativePlatform()) {
        log.info('Native platform detected')
        void this.checkShareIntent()
      }
    }
  }

  private async checkShareIntent(): Promise<void> {
    try {
      const { shareIntentService } = await import('../../services/import/share-intent-service.js')
      await shareIntentService.checkForSharedContent()
    } catch (err) {
      log.error('checkShareIntent failed:', err)
    }
  }

  private async loadInitialData(): Promise<void> {
    // Load initial app data
    log.info('Loading initial data...')

    // Initialize theme from saved settings
    try {
      const settingsStorage = new SettingsStorage()
      const settings = settingsStorage.getSettingsWithDefaults()
      themeManager.initializeFromSettings(settings.theme)
    } catch (error) {
      log.error('Failed to load theme settings:', error)
      // Fall back to auto theme
      themeManager.initializeFromSettings('auto')
    }

    // No artificial delay — load as fast as possible
  }

  private handleNavigateBack(): void {
    router.back()
  }

  private handleSettingsUpdate(event: CustomEvent): void {
    // Propagate settings update to all child components
    // The map component will receive this via its event listener
    log.debug('Settings updated:', event.detail)
  }

  private handleLocationSearch(event: CustomEvent): void {
    // Forward the location search event to the map component
    const mapComponent = this.shadowRoot?.querySelector('cairn-map')
    if (mapComponent) {
      const { coordinates, bounds, location, osmType, osmId, icon, source } = event.detail

      // Create a custom event to communicate with the map
      const locationEvent = new CustomEvent('location-search-navigation', {
        detail: { coordinates, bounds, location, osmType, osmId, icon, source },
      })
      mapComponent.dispatchEvent(locationEvent)
    }
  }

  private handleMobileLocationSearch(event: CustomEvent): void {
    // Forward the mobile navigation search event to the map component
    const mapComponent = this.shadowRoot?.querySelector('cairn-map')
    if (mapComponent) {
      // Stop the event from bubbling further to prevent double handling
      event.stopPropagation()

      // Forward the event directly to the map component
      const locationEvent = new CustomEvent('location-search-navigation', {
        detail: event.detail,
      })
      mapComponent.dispatchEvent(locationEvent)
    }
  }

  override updated(): void {
    if (!this.initComplete) return

    // Process any pending import once auth is resolved
    void this.handlePendingImportReady()

    if (this.loginModalChecked || this.loginModalDismissed) return

    const modal = this.shadowRoot?.querySelector('login-modal') as
      | (HTMLElement & { show(): Promise<void> })
      | null
    if (!modal) return

    this.loginModalChecked = true

    if (!authConfig.isConfigured) return
    if (this.authState.status === 'authenticated') return
    if (this.authState.status === 'authenticating') return
    if (authService.hasPriorSession) return
    if (window.location.pathname.startsWith('/auth/')) return

    void modal.show()
  }

  private handleLoginModalDismissed(): void {
    this.loginModalDismissed = true
    void this.processPendingImport()
  }

  private async autoPromptMissingPermissions(): Promise<void> {
    const status = await permissionManager.checkAllPermissions()
    if (status.location !== 'granted') {
      log.info('Auto-prompting for missing location permission')
      await permissionManager.requestAllPermissions()
    }
  }

  private boundHandlePendingImport = (): void => {
    void this.handlePendingImportReady()
  }

  // A cross-device settings pull (sync-manager) refreshed the local settings mirror;
  // re-init the theme from it (other readers re-read on next access / via the map).
  private boundHandleUserSettingsSynced = (): void => {
    try {
      const settings = new SettingsStorage().getSettingsWithDefaults()
      themeManager.initializeFromSettings(settings.theme)
    } catch (error) {
      log.error('Failed to re-init theme after settings sync:', error)
    }
  }

  private async handlePendingImportReady(): Promise<void> {
    if (!this.initComplete) return

    const { pendingImportService } = await import('../../services/import/pending-import-service.js')
    if (!pendingImportService.hasPending()) return

    if (this.authState.status === 'authenticated' || this.loginModalDismissed) {
      void this.processPendingImport()
      return
    }

    if (this.authState.status === 'authenticating') return

    const modal = this.shadowRoot?.querySelector('login-modal') as
      | (HTMLElement & { show(): Promise<void> })
      | null
    if (modal && !this.loginModalChecked) {
      this.loginModalChecked = true
      void modal.show()
    }
  }

  private async processPendingImport(): Promise<void> {
    if (this.pendingImportHandled) return
    const { pendingImportService } = await import('../../services/import/pending-import-service.js')
    if (!pendingImportService.hasPending()) return

    if (window.location.pathname !== '/') {
      router.navigate('/')
    }

    const mapEl = await this.waitForMapElement()
    if (!mapEl) return

    this.pendingImportHandled = true
    const file = pendingImportService.take()
    if (file) {
      mapEl.importPendingFile(file)
    }
  }

  private waitForMapElement(): Promise<
    (HTMLElement & { importPendingFile(file: File): void }) | null
  > {
    return new Promise(resolve => {
      let attempts = 0
      const check = (): void => {
        const el = this.shadowRoot?.querySelector('cairn-map') as
          | (HTMLElement & { importPendingFile(file: File): void })
          | undefined
        if (el) {
          resolve(el)
        } else if (attempts++ < 20) {
          requestAnimationFrame(check)
        } else {
          log.error('Map element not found for pending import')
          resolve(null)
        }
      }
      check()
    })
  }

  private retry(): void {
    this.error = null
    void this.initTask.run()
  }

  private async ensureViewLoaded(component: string): Promise<void> {
    if (loadedViews.has(component)) {
      return
    }
    const loader = viewImports[component]
    if (loader) {
      await loader()
      loadedViews.add(component)
    }
  }

  private renderCurrentView(): unknown {
    if (!this.currentRoute) {
      return html`<cairn-map></cairn-map>`
    }

    const { component } = this.currentRoute.route
    const { params } = this.currentRoute

    // Lazy-load the view component if needed
    if (!loadedViews.has(component)) {
      void this.ensureViewLoaded(component).then((): void => this.requestUpdate())
      return html``
    }

    switch (component) {
      case 'cairn-map':
        return html`<cairn-map></cairn-map>`
      case 'plan-map':
        return html`<plan-map .planId=${params.id}></plan-map>`
      case 'plan-download':
        return html`<plan-download .planId=${params.id}></plan-download>`
      case 'cairn-logbook': {
        const path = window.location.pathname
        const tab = path.startsWith('/plans')
          ? 'plans'
          : path.startsWith('/routes')
            ? 'routes'
            : 'activities'
        return html`<cairn-logbook .tab=${tab}></cairn-logbook>`
      }
      case 'plan-form': {
        const isEdit = !!params.id
        const sourceRouteId =
          new URLSearchParams(window.location.search).get('routeId') ?? undefined
        return html`<plan-form
          .editPlanId=${isEdit ? params.id : undefined}
          .sourceRouteId=${sourceRouteId}
        ></plan-form>`
      }
      case 'plan-detail': {
        const path = window.location.pathname
        const tab = path.endsWith('/pack-list')
          ? 'pack-list'
          : path.endsWith('/conditions')
            ? 'conditions'
            : path.endsWith('/field-log')
              ? 'field-log'
              : path.endsWith('/decisions')
                ? 'decisions'
                : 'overview'
        return html`<plan-detail .planId=${params.id} .tab=${tab}></plan-detail>`
      }
      case 'activity-detail':
        return html`<activity-detail .activityId=${params.id}></activity-detail>`
      case 'activity-map-page':
        return html`<activity-map-page .activityId=${params.id}></activity-map-page>`
      case 'route-detail':
        return html`<route-detail .routeId=${params.id}></route-detail>`
      case 'cairn-teams':
        return html`<cairn-teams></cairn-teams>`
      case 'cairn-team-detail':
        return html`<cairn-team-detail .teamId=${params.id}></cairn-team-detail>`
      case 'cairn-team-invite-claim':
        return html`<cairn-team-invite-claim .token=${params.token}></cairn-team-invite-claim>`
      case 'cairn-settings':
        return html`<cairn-settings .page=${window.location.pathname}></cairn-settings>`
      case 'subscription-success':
        return html`<subscription-success></subscription-success>`
      case 'auth-login':
        return html`<auth-login></auth-login>`
      case 'auth-register':
        return html`<auth-register></auth-register>`
      case 'auth-account-setup':
        return html`<auth-account-setup></auth-account-setup>`
      case 'eula-acceptance':
        return html`<eula-acceptance></eula-acceptance>`
      case 'auth-callback':
        return html`<auth-callback></auth-callback>`
      case 'auth-register-passkey':
        return html`<auth-register-passkey></auth-register-passkey>`
      case 'cairn-docs':
        return html`<cairn-docs path="/docs/"></cairn-docs>`
      default:
        return html`<cairn-map></cairn-map>`
    }
  }

  override render(): unknown {
    return html`
      ${this.error
        ? html`
            <div class="error">
              <div class="error-message">${this.error}</div>
              <button class="retry-button" @click=${this.retry}>Retry</button>
            </div>
          `
        : ''}

      <header class="app-header">
        <a class="app-logo" href="/about/" @click=${this.handleLogoClick}>
          <img src="/images/cairn-icon.png" alt="Taselith" />
          ${this.getCurrentView() === 'map'
            ? html` <span class="app-logo-title">Taselith</span> `
            : ''}
        </a>
        <div class="app-title-area">
          ${this.getCurrentView() !== 'map'
            ? html`
                <div class="title-content">
                  <div class="title-left">
                    ${this.needsBackButton()
                      ? html`
                          <button
                            class="back-button"
                            @click=${this.handlePageBack}
                            aria-label="Go back"
                            title="Go back"
                          >
                            <span class="material-icons">arrow_back</span>
                          </button>
                        `
                      : ''}
                    <h1 class="app-page-title">${this.getCurrentPageTitle()}</h1>
                  </div>
                </div>
              `
            : ''}
        </div>
        <div class="app-header-actions">
          ${this.getCurrentView() === 'map'
            ? html`
                <div class="header-search">
                  <location-search
                    label="Search"
                    placeholder="Search locations..."
                    direction="down"
                    .options=${{
                      maxResults: 6,
                      includeCoordinateParsing: true,
                      minQueryLength: 2,
                    }}
                    @location-selected=${this.handleLocationSearch}
                  ></location-search>
                </div>
              `
            : ''}
          <div class="profile-anchor" id="header-profile-anchor">
            <button
              class="profile-button"
              @click=${(): void => {
                this.showProfileMenu = !this.showProfileMenu
              }}
              aria-label="Profile"
              title="Profile"
            >
              ${this.authState.picture
                ? html`<img class="profile-avatar" src="${this.authState.picture}" alt="" />`
                : html`<span class="material-icons">person</span>`}
            </button>
            <md-menu
              anchor="header-profile-anchor"
              positioning="popover"
              anchor-corner="end-end"
              menu-corner="start-end"
              .open=${this.showProfileMenu}
              @closed=${(): void => {
                this.showProfileMenu = false
              }}
            >
              <md-menu-item
                @click=${(): void => {
                  this.showProfileMenu = false
                  router.navigate('/settings')
                }}
              >
                <span
                  class="material-icons"
                  slot="start"
                  style="font-family: 'Material Symbols Outlined';"
                  >person</span
                >
                <span slot="headline">Profile</span>
              </md-menu-item>
              ${this.authState.status === 'authenticated'
                ? html`
                    <md-menu-item
                      @click=${(): void => {
                        this.showProfileMenu = false
                        void authService.logout()
                      }}
                    >
                      <span
                        class="material-icons"
                        slot="start"
                        style="font-family: 'Material Symbols Outlined';"
                        >logout</span
                      >
                      <span slot="headline">Sign out</span>
                    </md-menu-item>
                  `
                : html`
                    <md-menu-item
                      @click=${(): void => {
                        this.showProfileMenu = false
                        void authService.login()
                      }}
                    >
                      <span
                        class="material-icons"
                        slot="start"
                        style="font-family: 'Material Symbols Outlined';"
                        >login</span
                      >
                      <span slot="headline">Sign in</span>
                    </md-menu-item>
                  `}
            </md-menu>
          </div>
        </div>
      </header>

      <main class="app-main">
        <div class="app-content">${this.renderCurrentView()}</div>

        <nav class="app-navigation">
          <cairn-navigation
            .currentView=${this.getCurrentView()}
            @navigate=${(e: CustomEvent): void => {
              const path = e.detail.path ?? viewToPath[e.detail.view] ?? '/'
              router.navigate(path)
            }}
          >
          </cairn-navigation>
        </nav>
      </main>
      <cairn-toast></cairn-toast>
      <login-modal @login-modal-dismissed=${this.handleLoginModalDismissed}></login-modal>
    `
  }

  private getCurrentView(): string {
    if (!this.currentRoute) {
      return 'map'
    }
    return getCurrentView(window.location.pathname)
  }

  private getCurrentPageTitle(): string {
    return getCurrentPageTitle(window.location.pathname)
  }

  private needsBackButton(): boolean {
    return needsBackButton(window.location.pathname)
  }

  private handlePageBack(): void {
    router.back()
  }

  private handleLogoClick(e: Event): void {
    const cap = (window as unknown as Record<string, unknown>).Capacitor as
      | { isNativePlatform?: () => boolean }
      | undefined
    if (cap?.isNativePlatform?.()) {
      e.preventDefault()
      router.navigate('/')
    }
  }
}
